import React, { useState, useEffect } from "react";
import { useAuth } from "../utils/useAuth";
import { db } from "../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, where, updateDoc } from "firebase/firestore";
import { createManagerNotification } from "../utils/notifications";

export default function ManageFields() {
  const { user, profile } = useAuth();
  const [fields, setFields] = useState([]);
  const [venue, setVenue] = useState("");
  const [fieldName, setFieldName] = useState("");

  useEffect(() => {
    loadFields();
  }, [user]);

  async function loadFields() {
    if (!user) return;
    
    const q = query(collection(db, "fields"), where("createdBy", "==", user.uid));
    const snap = await getDocs(q);
    setFields(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function addField(e) {
    e.preventDefault();
    
    if (!venue || !fieldName) {
      alert("Please fill in both venue and field name");
      return;
    }

    try {
      await addDoc(collection(db, "fields"), {
        venue,
        fieldName,
        createdBy: user.uid,
        approved: false,
        createdAt: serverTimestamp(),
      });

      // Notify managers about new field request
      await createManagerNotification(
        "New Field/Venue Request",
        `${profile.fullName} has submitted a new field for approval: ${fieldName} at ${venue}.`,
        profile.union
      );

      alert("Field submitted for approval!");
      setVenue("");
      setFieldName("");
      loadFields();
    } catch (err) {
      alert("Error adding field: " + err.message);
    }
  }

  async function deleteField(id) {
    if (!window.confirm("Are you sure you want to delete this field?")) return;

    try {
      await deleteDoc(doc(db, "fields", id));
      alert("Field deleted");
      loadFields();
    } catch (err) {
      alert("Error deleting field: " + err.message);
    }
  }

  async function resubmitField(id) {
    if (!window.confirm("Resubmit this field for approval?")) return;

    try {
      await updateDoc(doc(db, "fields", id), {
        rejected: false,
        rejectionReason: null,
        rejectedBy: null,
        rejectedAt: null,
        approved: false,
        resubmittedAt: serverTimestamp(),
      });
      alert("Field resubmitted for approval!");
      loadFields();
    } catch (err) {
      alert("Error resubmitting field: " + err.message);
    }
  }

  // Group fields by venue
  const fieldsByVenue = fields.reduce((acc, field) => {
    if (!acc[field.venue]) {
      acc[field.venue] = [];
    }
    acc[field.venue].push(field);
    return acc;
  }, {});

  // Separate pending, approved and rejected
  const pendingFields = fields.filter(f => !f.approved && !f.rejected);
  const approvedFields = fields.filter(f => f.approved);
  const rejectedFields = fields.filter(f => f.rejected);

  const approvedByVenue = approvedFields.reduce((acc, field) => {
    if (!acc[field.venue]) {
      acc[field.venue] = [];
    }
    acc[field.venue].push(field);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Manage Venue Fields</h2>

      {/* ADD NEW FIELD FORM */}
      <div className="bg-white p-6 rounded shadow mb-8">
        <h3 className="text-lg font-semibold mb-4">Submit New Field for Approval</h3>
        <form onSubmit={addField} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Venue Name *</label>
            <input
              className="w-full p-2 border rounded"
              placeholder="e.g., Loftus Versfeld"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Field Name *</label>
            <input
              className="w-full p-2 border rounded"
              placeholder="e.g., Field 1, Main Field, Training Ground A"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              required
            />
          </div>

          <button className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700">
            Add Field
          </button>
        </form>
      </div>

      {/* PENDING FIELDS */}
      {pendingFields.length > 0 && (
        <div className="bg-white p-6 rounded shadow mb-8">
          <h3 className="text-lg font-semibold mb-4 text-yellow-700">Pending Approval</h3>
          <div className="space-y-3">
            {pendingFields.map((field) => (
              <div
                key={field.id}
                className="flex justify-between items-center bg-yellow-50 p-3 rounded border-l-4 border-yellow-500"
              >
                <div>
                  <span className="font-semibold">{field.venue}</span>
                  <span className="text-gray-600"> - {field.fieldName}</span>
                </div>
                <span className="text-xs text-yellow-700 font-semibold">Awaiting Manager Approval</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REJECTED FIELDS */}
      {rejectedFields.length > 0 && (
        <div className="bg-white p-6 rounded shadow mb-8">
          <h3 className="text-lg font-semibold mb-4 text-red-700">Rejected Requests</h3>
          <div className="space-y-3">
            {rejectedFields.map((field) => (
              <div
                key={field.id}
                className="bg-red-50 p-4 rounded border-l-4 border-red-500"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-semibold">{field.venue}</span>
                    <span className="text-gray-600"> - {field.fieldName}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => resubmitField(field.id)}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    >
                      Resubmit
                    </button>
                    <button
                      onClick={() => deleteField(field.id)}
                      className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="bg-white p-3 rounded border border-red-300">
                  <p className="text-xs font-semibold text-red-700 mb-1">
                    Rejection Reason {field.rejectedBy && `(by ${field.rejectedBy})`}:
                  </p>
                  <p className="text-sm text-gray-700">{field.rejectionReason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* APPROVED FIELDS */}
      <div className="bg-white p-6 rounded shadow">
        <h3 className="text-lg font-semibold mb-4 text-green-700">Approved Fields</h3>

        {Object.keys(approvedByVenue).length === 0 ? (
          <p className="text-gray-500">No approved fields yet.</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(approvedByVenue).map(([venueName, venueFields]) => (
              <div key={venueName} className="border-l-4 border-green-600 pl-4">
                <h4 className="font-bold text-lg mb-2">{venueName}</h4>
                <div className="space-y-2">
                  {venueFields.map((field) => (
                    <div
                      key={field.id}
                      className="flex justify-between items-center bg-gray-50 p-3 rounded"
                    >
                      <span>{field.fieldName}</span>
                      <button
                        onClick={() => deleteField(field.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
